import { useCallback, useEffect, useState } from 'react'

import {
  AriaInteractionMode,
  DEFAULT_ARIA_INTERACTION_MODE,
  MediaPermissionState,
  isSpokenAriaMode
} from '../../../../pure-js/types/AppAssistant'
import { checkMicrophonePermission } from '../components/AppAssistant/realtime/assistantDiagnostics'

// Device-scoped, not user-scoped: the right mode depends on where you are, not on who you are.
const STORAGE_KEY = 'intraction_aria_interaction_mode'

const SUPPORTED_MODES: string[] = Object.values(AriaInteractionMode)

function readStoredMode(): AriaInteractionMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return SUPPORTED_MODES.includes(stored || '') ? (stored as AriaInteractionMode) : DEFAULT_ARIA_INTERACTION_MODE
  } catch {
    // Private mode / storage disabled — the default is a perfectly good answer.
    return DEFAULT_ARIA_INTERACTION_MODE
  }
}

export type UseAriaInteractionModeParams = {
  /**
   * Whether the choice outlives the visit. The floating assistant remembers it; the coach does not —
   * voice is the product there, so it opens spoken every time. One shared key meant typing once in
   * either surface silently changed how the other opened.
   */
  remember: boolean
}

export type UseAriaInteractionModeResult = {
  mode: AriaInteractionMode
  setMode: (mode: AriaInteractionMode) => void
  /**
   * True only when a spoken mode is selected AND the browser already holds microphone permission.
   * Undefined until the permission has been read, so callers can wait instead of flashing the primer.
   */
  canAutoStartVoice?: boolean
}

/**
 * The chosen way of talking to Aria, shared by the floating assistant and the 1:1 coach. Auto-start
 * waits until the microphone is already granted, so a permission prompt never greets the user.
 */
export function useAriaInteractionMode({ remember }: UseAriaInteractionModeParams): UseAriaInteractionModeResult {
  const [mode, setModeState] = useState<AriaInteractionMode>(() =>
    remember ? readStoredMode() : DEFAULT_ARIA_INTERACTION_MODE
  )
  const [micPermission, setMicPermission] = useState<MediaPermissionState | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    // Permission only: waiting on the device list too is what left "allow the microphone" on screen
    // for seconds after someone had already allowed it.
    void checkMicrophonePermission().then((permission) => {
      if (!cancelled) setMicPermission(permission)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const setMode = useCallback(
    (next: AriaInteractionMode) => {
      setModeState(next)
      if (!remember) return
      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // Not being able to remember the choice is not worth failing the interaction over.
      }
    },
    [remember]
  )

  const canAutoStartVoice =
    micPermission === undefined ? undefined : isSpokenAriaMode(mode) && micPermission === MediaPermissionState.GRANTED

  return { mode, setMode, canAutoStartVoice }
}
